import { Injectable } from '@angular/core';
import * as blend from 'mixpanel-browser';

import { environment } from '../../../environments/environment';
import { SkuService } from './sku.service';
import { DefaultProperties, BlendBroadcastPayload, User, BlendEvent } from '../models';
import { BroadcastService } from './broadcast.service';
import { Subscription } from 'rxjs';
import { Store, select } from '@ngrx/store';
import * as fromRoot from '../../reducers';
import { isUrlHistoryClean, linkURLSanitizer } from 'src/app/util/blend-helper';

@Injectable({
  providedIn: 'root',
})
export class BlendService {
  private blend: any;
  private userSubscribe: Subscription;
  private user: User;

  constructor(
    private skuService: SkuService,
    private broadcastService: BroadcastService,
    private store: Store<fromRoot.State>,
  ) {
    this.blend = blend.init(
      environment.blendToken,
      {
        persistence: 'localStorage', //This prevents getting a "Cookie Too Large" error and in general is a more reliable way to persist state on the browser.
        api_host: environment.blendHost,
        ...(!environment.production && { debug: true }),
      },
      'Sync',
    );

    this.broadcastService.on('sync.reset.user').subscribe(() => {
      this.reset();
    });
    this.broadcastService.on('sync.track.blend').subscribe((payload: BlendBroadcastPayload) => {
      this.track(payload.eventName, payload.parameters);
    });
  }

  public async identifyUser(newUserId?: number): Promise<void> {
    let userId = null;
    if (newUserId) {
      userId = newUserId;
    } else {
      userId = this.user.id;
    }
    if (userId) {
      if (this.blend.get_distinct_id() != userId) {
        this.blend.reset();
        // Set a super property so that the URLs don't get posted with identify events
        this.blend.register({
          $current_url: linkURLSanitizer(window.location.href),
          $referrer: linkURLSanitizer(document.referrer),
        });
        this.blend.identify(userId);
        this.blend.unregister('$current_url');
        this.blend.unregister('$referrer');
      }
    }
  }

  public async track(event: string, parameters: any = {}): Promise<void> {
    await this.getUser();
    const isBlendAllowed = (this.user && this.user.is_disableanalytics != 1) || event == BlendEvent.ANALYTICS_OPT_IN;
    if (!isBlendAllowed) {
      return;
    }
    await this.identifyUser();
    this.blend.track(event, {
      ...this.getDefaultProperties(),
      ...parameters,
    });
  }

  public async trackSignUp(newUserId: number, parameters: any = {}): Promise<void> {
    await this.identifyUser(newUserId);
    this.blend.track(BlendEvent.SIGN_UP, {
      ...this.getDefaultProperties(),
      ...parameters,
    });
  }

  public async trackPurchaseCompleted(newUserId: number, parameters: any = {}): Promise<void> {
    await this.identifyUser(newUserId);
    this.blend.track(BlendEvent.PURCHASE_COMPLETED, {
      ...this.getDefaultProperties(),
      ...parameters,
    });
  }

  public async setUserProperty(obj): Promise<void> {
    const id = this.user.id;
    this.blend.identify(id);
    this.blend.people.set({ ...obj });
  }

  public async trackPageview(): Promise<void> {
    await this.getUser();
    const isBlendAllowed = this.user && this.user.is_disableanalytics != 1;
    if (!isBlendAllowed) {
      return;
    }
    await this.identifyUser();
    this.blend.track_pageview(this.getDefaultProperties());
  }

  public optOut(): void {
    this.blend.opt_out_tracking();
  }

  public reset(): void {
    this.blend.reset();
    this.user = null;
  }

  public getDefaultProperties(): DefaultProperties {
    const pathname = window.location.pathname;

    if (!isUrlHistoryClean()) {
      const defaultProperties: DefaultProperties = {
        current_page_title: '-',
        current_url_path: '-',
        current_url_search: '-',
        $current_url: linkURLSanitizer(window.location.href), // Sanitize the URL if it's a link
        platform: 'sync-new-ui',
        $referrer: linkURLSanitizer(document.referrer),
      };
      return defaultProperties;
    }

    // Static titles based on known routes
    const staticTitles: { [key: string]: string } = {
      '/files': 'Files | Sync.com',
      '/file': 'File | Sync.com',
      '/vault': 'Vault | Sync.com',
    };

    for (const key in staticTitles) {
      if (pathname.startsWith(key)) {
        return {
          current_page_title: staticTitles[key],
          current_url_path: pathname,
          platform: 'sync-new-ui',
        };
      }
    }

    // Default fallback for clean URLs
    return {
      current_url_path: pathname,
      platform: 'sync-new-ui',
    };
  }

  private async getUser(): Promise<any> {
    return await new Promise((resolve) => {
      if (this.user) {
        if (this.userSubscribe) {
          this.userSubscribe.unsubscribe();
        }
        resolve(true);
        return;
      }
      this.userSubscribe = this.store.pipe(select(fromRoot.getAuthUser)).subscribe((data) => {
        this.user = data;
        if (this.userSubscribe) {
          this.userSubscribe.unsubscribe();
        }
        resolve(true);
      });
    });
  }

  /**
   * I have add this function as it is intended to be used in multiple click events
   */
  public async addEventsForUpgradeButton(): Promise<void> {
    const sku = await this.skuService.getSku();
    this.track(BlendEvent.UPGRADE_INQUIRY, {
      plan: sku,
      plan_type: await this.skuService.getPlanType(sku),
    });
  }

  public trackUnauthPageview(): void {
    this.blend.track_pageview(this.getDefaultProperties());
  }
}
